import { Component } from "react"
import PropTypes from "prop-types"

import * as DiffUtils from "./diff_utils"
import TriggerField from "./trigger_field"
import Ingredient from "./ingredient"
import AppletDefault from "./applet_default"
import { StatusRow } from "./table_components"

const APPLET_DEFAULT_GROUPS = [
  {
    title: "Send a notification",
    fields: ["mobile"],
  },
  {
    title: "Post a short message",
    fields: ["message"],
  },
  {
    title: "Create a text post",
    fields: ["title", "html", "tags"],
  },
  {
    title: "Create a text file",
    fields: ["filename", "folder", "body"],
  },
  {
    title: "Add row to spreadsheet",
    fields: ["collection_title", "spreadsheet"],
  },
  {
    title: "Call my phone",
    fields: ["spoken"],
  },
  {
    title: "Quick add event",
    fields: ["calendar"],
  },
]

class Trigger extends Component {
  constructor(props) {
    super(props)
    this.pathObjs = ["channel", "triggers", this.props.trigger.id]
  }

  renderAttributes() {
    const { trigger, feedbackItems = {}, service } = this.props
    const serviceSlug =
      trigger.type === "deleted"
        ? this.props.originalChannel.slug
        : service.slug
    let fields = [
      {
        key: `trigger-name-${trigger.id}`,
        name: "Name",
        required: true,
        value: (
          <a
            target="_blank"
            rel="noreferrer"
            href={`/services/${serviceSlug}/triggers/${trigger.slug}`}
          >
            {trigger.name}
          </a>
        ),
        fieldName: "name",
      },
    ]
    if (this.props.status !== "deleted") {
      fields = fields.concat([
        {
          key: `trigger-description-${trigger.id}`,
          name: "Description",
          required: true,
          value: trigger.description,
          fieldName: "description",
        },
        {
          key: `trigger-verbiage-${trigger.id}`,
          name: "Verbiage",
          value: trigger.verbiage,
          fieldName: "verbiage",
        },
        {
          key: `trigger-slug-${trigger.id}`,
          name: "Endpoint",
          required: true,
          value: trigger.slug,
          transform: value => `/${value}`,
          fieldName: "slug",
        },
      ])
    }
    const submenuIcons = JSON.parse(window.sessionStorage.submenuIcons)
    return fields.map(field => {
      field.diff = this.props.changes.find(
        diff =>
          diff.data &&
          diff.data.property === field.fieldName &&
          diff.path.length === 2
      )
      if (feedbackItems[field.fieldName]) {
        field.feedback = feedbackItems[field.fieldName]
        if (!submenuIcons.triggersFeedback) {
          this.props.setSessionFeedback("triggers", submenuIcons)
        }
      }
      field.pathObjs = [...this.pathObjs, field.fieldName]
      return this.props.tableRow(field)
    })
  }

  prepareCollectionForRender = (items, collection) => {
    const { changes, trigger, diffStyle } = this.props
    if (diffStyle !== "none") {
      const itemsDeleted = DiffUtils.collectionDeletedItems(
        changes,
        this.props.originalChannel,
        ["triggers", trigger, collection]
      )
      items = items.concat(itemsDeleted)
    }
    return DiffUtils.setupCollectionForRender(changes, items, [
      "triggers",
      trigger,
      collection,
    ])
  }

  render() {
    const { changes, trigger, feedbackItems = {} } = this.props
    const triggerFieldFeedback = feedbackItems.trigger_fields || {}
    const ingredientFeedback = feedbackItems.ingredients || {}
    const appletDefaultFeedback =
      (feedbackItems.example_recipes &&
        feedbackItems.example_recipes.stencil) ||
      {}

    const fieldsReordered = DiffUtils.collectionWasReordered(changes, [
      "triggers",
      trigger,
      "trigger_fields",
    ])

    const triggerFields = this.prepareCollectionForRender(
      trigger.trigger_fields,
      "trigger_fields"
    )
    const ingredients = this.prepareCollectionForRender(
      trigger.ingredients,
      "ingredients"
    )
    const appletDefaults = APPLET_DEFAULT_GROUPS.map(template => {
      const stencil = { title: template.title }
      const fields = template.fields.map(slug =>
        trigger.stencil.fields.find(field => field.slug === slug)
      )
      stencil.fields = DiffUtils.setupCollectionForRender(changes, fields, [
        "triggers",
        trigger,
        "stencil",
        "fields",
      ])
      stencil.fields.forEach(
        field => (field.feedback = appletDefaultFeedback[field.id])
      )
      stencil.status = stencil.fields.find(field => field.type === "updated")
        ? "updated"
        : "stale"
      return stencil
    })

    return (
      <table>
        <tbody>
          {this.props.diffStyle !== "none" && (
            <StatusRow status={this.props.status} />
          )}
          {this.props.diffStyle !== "none" && fieldsReordered && (
            <tr>
              <td colSpan="3" className="reordered">
                Trigger fields have been reordered.
              </td>
            </tr>
          )}
          {this.renderAttributes()}
          {this.props.status !== "deleted" && (
            <tr>
              <th>Trigger fields</th>
              <td>
                {triggerFields.length > 0
                  ? triggerFields.map(f => (
                      <TriggerField
                        key={`trigger-field-${f.id}`}
                        originalChannel={this.props.originalChannel}
                        diffStyle={this.props.diffStyle}
                        triggerField={f}
                        changes={f.changes}
                        status={f.type}
                        pathObjs={[...this.pathObjs, "trigger_fields"]}
                        feedbackItems={triggerFieldFeedback[f.id]}
                        tableRow={this.props.tableRow}
                        setSessionFeedback={this.props.setSessionFeedback}
                      />
                    ))
                  : "None"}
              </td>
              <td className="no-feedback" />
            </tr>
          )}
          {this.props.status !== "deleted" && (
            <tr>
              <th>Ingredients</th>
              <td>
                {ingredients.length > 0
                  ? ingredients.map(ing => (
                      <Ingredient
                        key={`trigger-ingredient-${ing.id}`}
                        type="trigger"
                        diffStyle={this.props.diffStyle}
                        ingredient={ing}
                        changes={ing.changes}
                        status={ing.type}
                        pathObjs={[...this.pathObjs, "ingredients"]}
                        feedbackItems={ingredientFeedback[ing.id]}
                        tableRow={this.props.tableRow}
                      />
                    ))
                  : "None"}
              </td>
              <td className="no-feedback" />
            </tr>
          )}
          {this.props.status !== "deleted" && (
            <tr>
              <th>Applet Defaults</th>
              <td>
                {appletDefaults.map((stencil, index) => (
                  <AppletDefault
                    key={`applet-default-group-${trigger.id}-${index}`}
                    diffStyle={this.props.diffStyle}
                    stencil={stencil}
                    pathObjs={[...this.pathObjs, "example_recipes", "stencil"]}
                    tableRow={this.props.tableRow}
                  />
                ))}
              </td>
              <td className="no-feedback" />
            </tr>
          )}
        </tbody>
      </table>
    )
  }
}

Trigger.propTypes = {
  triggerUrl: PropTypes.string.isRequired,
  diffStyle: PropTypes.string.isRequired,
  service: PropTypes.object.isRequired,
  originalChannel: PropTypes.object,
  status: PropTypes.oneOf(["new", "deleted", "updated", "stale"]).isRequired,
  trigger: PropTypes.object.isRequired,
  changes: PropTypes.array.isRequired,
  feedbackItems: PropTypes.object,
  tableRow: PropTypes.func.isRequired, // Should be imported
  setSessionFeedback: PropTypes.func.isRequired,
}

export default Trigger
